// SPDX-License-Identifier: AGPL-3.0-or-later

// Live feed events from the gateway broadcast channel, fanned out to mounted feed views.

import type {GatewayHandlerContext} from '@app/features/gateway/events/EventRouter';
import type {SocialCommentResponse, SocialPostResponse} from '@fluxer/schema/src/domains/social/SocialPostSchemas';

export type SocialRealtimeEvent =
	| {type: 'post_create'; post: SocialPostResponse}
	| {
			type: 'post_update';
			id: string;
			like_count: number;
			comment_count: number;
			view_count: number;
			poll_counts: Array<number> | null;
	  }
	| {type: 'post_delete'; id: string}
	| {type: 'comment_create'; postId: string; comment: SocialCommentResponse}
	| {type: 'comment_delete'; postId: string; id: string}
	| {type: 'pending_update'; count: number};

type Listener = (event: SocialRealtimeEvent) => void;

const listeners = new Set<Listener>();

export function subscribeSocialRealtime(listener: Listener): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function emit(event: SocialRealtimeEvent): void {
	for (const listener of listeners) {
		try {
			listener(event);
		} catch {
			// One broken view must not stop the others.
		}
	}
}

function record(data: unknown): Record<string, unknown> | null {
	return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
}

const count = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

export function handleSocialPostCreate(data: unknown, _context: GatewayHandlerContext): void {
	const post = record(record(data)?.post);
	if (post && typeof post.id === 'string') emit({type: 'post_create', post: post as unknown as SocialPostResponse});
}

export function handleSocialPostUpdate(data: unknown, _context: GatewayHandlerContext): void {
	const value = record(data);
	if (!value || typeof value.id !== 'string') return;
	emit({
		type: 'post_update',
		id: value.id,
		like_count: count(value.like_count),
		comment_count: count(value.comment_count),
		view_count: count(value.view_count),
		poll_counts: Array.isArray(value.poll_counts) ? value.poll_counts.map(count) : null,
	});
}

export function handleSocialPostDelete(data: unknown, _context: GatewayHandlerContext): void {
	const value = record(data);
	if (value && typeof value.id === 'string') emit({type: 'post_delete', id: value.id});
}

export function handleSocialCommentCreate(data: unknown, _context: GatewayHandlerContext): void {
	const value = record(data);
	const comment = record(value?.comment);
	if (value && typeof value.post_id === 'string' && comment && typeof comment.id === 'string') {
		emit({type: 'comment_create', postId: value.post_id, comment: comment as unknown as SocialCommentResponse});
	}
}

export function handleSocialCommentDelete(data: unknown, _context: GatewayHandlerContext): void {
	const value = record(data);
	if (value && typeof value.post_id === 'string' && typeof value.id === 'string') {
		emit({type: 'comment_delete', postId: value.post_id, id: value.id});
	}
}

export function handleSocialPendingUpdate(data: unknown, _context: GatewayHandlerContext): void {
	const value = record(data);
	if (value && typeof value.count === 'number') emit({type: 'pending_update', count: value.count});
}
